// 051 — Historia Clínica Materna Perinatal: capa de conversión a recursos
// FHIR (Fase 4, Tanda 1a, ver PLAN.md "FASE 4 — spec completa"). Migrado de
// `extraerHistoriaMaternoPerinatal` (extractors.ts, Patrón A) — mismo
// mecanismo FHIR exacto (extensión `datos-gestacionales` del Patient +
// Observation de altura uterina/FCF por visita), solo cambia el origen del
// dato (este archivo lee del estado tipado de Patrón B, no de un
// `QuestionnaireResponse`). El resto de los campos de esta tanda (>60) es
// `Par[]` de respaldo en el PDF, sin recurso FHIR — ver
// `bloques051IdentificacionDatos.ts`.
import type { MedplumClient } from '@medplum/core';
import type { Encounter, Observation, Patient, Reference } from '@medplum/fhirtypes';
import { idTemporal, type LoteTransaccional } from './transaccionFhir';
import type { EstadoAntecedentesObstetricos051 } from './bloques051IdentificacionDatos';
import type { EstadoControlesPrenatales051 } from './bloques051ControlesDatos';

export const EXT_DATOS_GESTACIONALES = 'http://logiacare.ec/fhir/datos-gestacionales';
// La frecuencia cardíaca fetal no tiene un LOINC de uso tan inequívoco como
// los demás signos vitales — se deja en código local hasta verificarlo
// formalmente (mismo criterio y misma nota ya presente en el extractor
// legado de Patrón A).
const ALTURA_UTERINA_LOINC = '11881-0';

type SubExtensionGestacional = { readonly url: string; readonly valueDate: string } | { readonly url: string; readonly valueInteger: number };

/** Arma las sub-extensiones de `datos-gestacionales` a partir de los antecedentes obstétricos — pura, sin I/O, testeable sin mockear Medplum. */
export function subExtensionesDatosGestacionales(e: EstadoAntecedentesObstetricos051): readonly SubExtensionGestacional[] {
  const gestas = e.gestas.trim() === '' ? undefined : Number(e.gestas);
  const partos = e.partos.trim() === '' ? undefined : Number(e.partos);
  const abortos = e.abortos.trim() === '' ? undefined : Number(e.abortos);
  const cesareas = e.cesareas.trim() === '' ? undefined : Number(e.cesareas);
  return [
    e.fechaUltimaMenstruacion.trim() && { url: 'fum', valueDate: e.fechaUltimaMenstruacion.trim() },
    e.fechaProbableParto.trim() && { url: 'fpp', valueDate: e.fechaProbableParto.trim() },
    gestas !== undefined && !Number.isNaN(gestas) && { url: 'gestas', valueInteger: gestas },
    partos !== undefined && !Number.isNaN(partos) && { url: 'partos', valueInteger: partos },
    abortos !== undefined && !Number.isNaN(abortos) && { url: 'abortos', valueInteger: abortos },
    cesareas !== undefined && !Number.isNaN(cesareas) && { url: 'cesareas', valueInteger: cesareas },
  ].filter((s): s is SubExtensionGestacional => !!s);
}

/**
 * Agrega al lote la actualización del Patient con la extensión `datos-gestacionales` (reemplaza la anterior si existe) — no agrega nada si no hay ningún dato para guardar.
 * La lectura previa del Patient se hace aquí, ANTES de enviar el lote.
 */
export async function agregarDatosGestacionales(lote: LoteTransaccional, medplum: MedplumClient, subject: Reference<Patient>, antecedentesObstetricos: EstadoAntecedentesObstetricos051): Promise<void> {
  const subExtensiones = subExtensionesDatosGestacionales(antecedentesObstetricos);
  if (subExtensiones.length === 0) return;
  const paciente = await medplum.readReference(subject);
  const otrasExtensiones = (paciente.extension ?? []).filter((ext) => ext.url !== EXT_DATOS_GESTACIONALES);
  lote.actualizar<Patient & { id: string }>({ ...paciente, extension: [...otrasExtensiones, { url: EXT_DATOS_GESTACIONALES, extension: [...subExtensiones] }] } as Patient & { id: string });
}

/**
 * Agrega al lote un Observation de altura uterina por cada control prenatal que la traiga (mismo LOINC/forma que el extractor legado de Patrón A).
 * La fecha del control (si es AAAA-MM-DD) es la fecha efectiva; sin fecha, se usa `ahora`.
 * `fullUrls` (opcional): recibe el `urn:uuid:` de cada Observation creado, para colgarlos del panel de controles prenatales (`perinatal051/controles.ts`).
 */
export function agregarObservacionAlturaUterina(
  lote: LoteTransaccional,
  subject: Reference<Patient>,
  encounter: Reference<Encounter> | undefined,
  controlPrenatal: EstadoControlesPrenatales051,
  ahora: Date = new Date(),
  fullUrls?: string[]
): Observation[] {
  const creadas: Observation[] = [];
  for (const fila of controlPrenatal.filas) {
    const valor = fila.alturaUterina.trim() === '' ? undefined : Number(fila.alturaUterina.trim().replace(',', '.'));
    if (valor === undefined || Number.isNaN(valor)) continue;
    const fecha = /^\d{4}-\d{2}-\d{2}$/.test(fila.fecha.trim()) ? fila.fecha.trim() : ahora.toISOString();
    const fullUrl = fullUrls ? idTemporal() : undefined;
    if (fullUrl) fullUrls?.push(fullUrl);
    creadas.push(
      lote.crear<Observation>(
        {
        resourceType: 'Observation',
        status: 'final',
        code: { coding: [{ system: 'http://loinc.org', code: ALTURA_UTERINA_LOINC, display: 'Fundal height of uterus' }] },
        subject,
        encounter,
        effectiveDateTime: fecha,
        valueQuantity: { value: valor, unit: 'cm', system: 'http://unitsofmeasure.org', code: 'cm' },
        },
        fullUrl
      )
    );
  }
  return creadas;
}
